import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from './Modal';
import { SearchableSelect } from './SearchableSelect';
import { AsyncSearchableSelect } from './AsyncSearchableSelect';
import { InlineEquipmentTypeForm } from './InlineEquipmentTypeForm.js';
import { InlineCompanyForm } from './InlineCompanyForm.js';
import { buildEquipmentTypeOptions } from '@gng-ehs/shared/equipment-rules';
import { api, fetchCompanyOptions } from '../lib/api';

interface BulkEquipmentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (count: number) => void;
  initialCompanyId?: string;
}

export function BulkEquipmentModal({ open, onClose, onSuccess, initialCompanyId }: BulkEquipmentModalProps) {
  const [companyId, setCompanyId] = useState(initialCompanyId || '');
  const [equipmentType, setEquipmentType] = useState('');
  const [quantity, setQuantity] = useState<number>(10);
  const [tagPrefix, setTagPrefix] = useState('');
  const [swlValue, setSwlValue] = useState('');
  const [liftHeight, setLiftHeight] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [equipmentSize, setEquipmentSize] = useState('');
  const [locationInFactory, setLocationInFactory] = useState('');
  const [firstUseType] = useState('NOT_APPLICABLE');
  const [firstUseValue] = useState('');

  const [customTypes, setCustomTypes] = useState<Array<{ key: string; label: string; short_code?: string }>>([]);
  const [showInlineTypeForm, setShowInlineTypeForm] = useState(false);
  const [showCompanyForm, setShowCompanyForm] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialCompanyId) setCompanyId(initialCompanyId);
  }, [initialCompanyId]);

  useEffect(() => {
    api.equipmentTypes.list()
      .then((res) => {
        if (res.data) setCustomTypes(res.data.map((t) => ({ key: t.key, label: t.label, short_code: t.short_code })));
      })
      .catch(() => { /* ignore */ });
  }, []);

  const allTypeOptions = useMemo(() => {
    return buildEquipmentTypeOptions(customTypes);
  }, [customTypes]);

  const handleSuggestPrefix = () => {
    if (!companyId || !equipmentType) return;
    api.equipment.suggestTagName(companyId, equipmentType)
      .then((res) => {
        if (res.data?.prefix) {
          setTagPrefix(res.data.prefix);
        }
      })
      .catch(() => { /* ignore */ });
  };

  // Auto set prefix when equipmentType or companyId changes
  useEffect(() => {
    if (!companyId || !equipmentType) return;
    handleSuggestPrefix();
  }, [companyId, equipmentType]);

  const previewTags = useMemo(() => {
    if (!tagPrefix.trim() || !quantity || quantity <= 0) return [];
    const prefix = tagPrefix.trim().toUpperCase();
    const count = Math.min(quantity, 8); // show preview of first 8
    const tags: string[] = [];
    for (let i = 1; i <= count; i++) {
      tags.push(`${prefix}/${String(i).padStart(2, '0')}`);
    }
    if (quantity > 8) {
      tags.push(`… and ${quantity - 8} more`);
    }
    return tags;
  }, [tagPrefix, quantity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) {
      setError('Please select a Company.');
      return;
    }
    if (!equipmentType) {
      setError('Please select an Equipment Type.');
      return;
    }
    if (!quantity || quantity < 1 || quantity > 500) {
      setError('Quantity must be between 1 and 500.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const res = await api.equipment.bulkCreate({
        company_id: companyId,
        equipment_name_type: equipmentType,
        quantity: Number(quantity),
        tag_prefix: tagPrefix.trim() || undefined,
        swl_value: swlValue ? parseFloat(swlValue) : undefined,
        lift_height: liftHeight || undefined,
        make: make || undefined,
        model: model || undefined,
        equipment_size: equipmentSize || undefined,
        location_in_factory: locationInFactory || undefined,
        first_use_type: firstUseType,
        first_use_value: firstUseValue || '',
      });

      if (res.data) {
        onSuccess(res.data.count);
        onClose();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to bulk create equipment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="⚡ Bulk Create Similar Equipment" size="xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')}>✕</button>
          </div>
        )}

        <div className="bg-brand-50/60 p-3 rounded-xl border border-brand-200 text-xs text-brand-900 space-y-1">
          <p className="font-bold flex items-center gap-1.5 text-brand-800">
            <span>📦 Bulk Generator for Identical Equipment</span>
          </p>
          <p className="text-gray-600">
            Ideal for factories with 10, 50, or 100+ identical equipment items (e.g. Chain Pulley Blocks, Shackles).
            All items will copy the specifications below with auto-incremented Tag Names.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Company <span className="text-red-500">*</span></label>
              <button
                type="button"
                onClick={() => setShowCompanyForm(!showCompanyForm)}
                className="text-[11px] text-emerald-700 font-bold hover:underline cursor-pointer"
              >
                + New Company
              </button>
            </div>
            <AsyncSearchableSelect
              placeholder="Search company…"
              fetchOptions={fetchCompanyOptions}
              value={companyId}
              onChange={setCompanyId}
              isCompanySelect={true}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label mb-0">Equipment Type <span className="text-red-500">*</span></label>
              <button
                type="button"
                onClick={() => setShowInlineTypeForm(!showInlineTypeForm)}
                className="text-[11px] text-brand-700 font-bold hover:underline cursor-pointer"
              >
                + New Type
              </button>
            </div>
            <SearchableSelect
              placeholder="Select type…"
              options={allTypeOptions}
              value={equipmentType}
              onChange={setEquipmentType}
            />
            {showInlineTypeForm && (
              <InlineEquipmentTypeForm
                onCreated={(newType) => {
                  setCustomTypes((prev) => [...prev, newType]);
                  setEquipmentType(newType.key);
                  setShowInlineTypeForm(false);
                }}
                onCancel={() => setShowInlineTypeForm(false)}
              />
            )}
          </div>
          <div>
            <label className="label">Quantity (Items) <span className="text-red-500">*</span></label>
            <input
              type="number"
              min="1"
              max="500"
              className="input font-semibold"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
              required
            />
          </div>
        </div>

        {/* Inline Company Creation Form */}
        {showCompanyForm && (
          <InlineCompanyForm
            onCreated={(created) => {
              setCompanyId(created.id);
              setShowCompanyForm(false);
            }}
            onCancel={() => setShowCompanyForm(false)}
          />
        )}

        {/* Tag Name Prefix & Live Preview */}
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Tag Name Prefix</label>
            <div className="flex gap-2">
              <input
                type="text"
                className="input font-mono uppercase font-semibold flex-1"
                placeholder="e.g. SPL/CPB"
                value={tagPrefix}
                onChange={(e) => setTagPrefix(e.target.value.toUpperCase())}
              />
              <button
                type="button"
                onClick={handleSuggestPrefix}
                title="Suggest prefix for company & type"
                className="px-2.5 py-1 text-xs font-semibold bg-white text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg shrink-0 cursor-pointer"
              >
                Suggest
              </button>
            </div>
            <span className="text-[11px] text-gray-500 mt-0.5 block">Auto-prefix derived from {companyId ? 'selected company & ' : ''}type</span>
          </div>
          <div>
            <label className="label">Generated Tag Names Preview</label>
            <div className="flex flex-wrap gap-1.5 p-2 bg-white rounded border border-gray-200 min-h-[38px] items-center">
              {previewTags.length === 0 ? (
                <span className="text-xs text-gray-400">Select type & quantity to preview tags</span>
              ) : (
                previewTags.map((tag, idx) => (
                  <span key={idx} className="px-1.5 py-0.5 text-[11px] font-mono font-bold bg-brand-100 text-brand-800 rounded border border-brand-200">
                    {tag}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Specifications Grid */}
        <div className="space-y-3 pt-2">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Shared Specifications (Copied to all items)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label">SWL Capacity (Ton)</label>
              <input type="number" step="0.1" className="input" value={swlValue} onChange={(e) => setSwlValue(e.target.value)} placeholder="e.g. 5.0" />
            </div>
            <div>
              <label className="label">Lift Height</label>
              <input type="text" className="input" value={liftHeight} onChange={(e) => setLiftHeight(e.target.value)} placeholder="e.g. 6.0 Mtr" />
            </div>
            <div>
              <label className="label">Make / Manufacturer</label>
              <input type="text" className="input" value={make} onChange={(e) => setMake(e.target.value)} placeholder="e.g. Godrej" />
            </div>
            <div>
              <label className="label">Model Number</label>
              <input type="text" className="input" value={model} onChange={(e) => setModel(e.target.value)} />
            </div>
            <div>
              <label className="label">Equipment Size</label>
              <input type="text" className="input" value={equipmentSize} onChange={(e) => setEquipmentSize(e.target.value)} />
            </div>
            <div>
              <label className="label">Location in Factory</label>
              <input type="text" className="input" value={locationInFactory} onChange={(e) => setLocationInFactory(e.target.value)} placeholder="e.g. Bay 2 Shop Floor" />
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex justify-between items-center">
            <span>{error}</span>
            <button type="button" onClick={() => setError('')}>✕</button>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button type="button" onClick={onClose} className="btn btn-secondary text-xs h-9 whitespace-nowrap shrink-0">Cancel</button>
          <button type="submit" disabled={saving || !companyId || !equipmentType || quantity < 1} className="btn btn-primary text-xs h-9 px-5 whitespace-nowrap shrink-0 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? 'Creating Equipment Items…' : `⚡ Generate ${quantity} Equipment Items`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
